import type { WebModule } from "./module.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Starting and stopping modules' background work.

   The shell calls this once a person is signed in and calls the returned
   function when they sign out, or when what they may do changes. It lives
   apart from the shell, with the permission check passed in, so the rule
   "nothing starts without its permission" is a plain function a test can run
   without React or a browser.
   ────────────────────────────────────────────────────────────────────────── */

export type BackgroundContext = {
  /** The signed-in person. Anything a background remembers per person keys on this. */
  userId: string;
};

/** The ids of the modules whose background this person may run, in registry order. */
export function allowedBackgrounds(modules: WebModule[], can: (needs: string) => boolean): string[] {
  return modules.flatMap((m) => (m.background && can(m.background.needs) ? [m.id] : []));
}

/**
 * Starts every background the person may run and returns one function that
 * stops them all. One that throws while starting is skipped rather than
 * taking the others down with it: a broken notifier must not stop a sync.
 */
export function startBackgrounds(modules: WebModule[], can: (needs: string) => boolean, ctx: BackgroundContext): () => void {
  const allowed = new Set(allowedBackgrounds(modules, can));
  const stops: (() => void)[] = [];
  for (const m of modules) {
    if (!m.background || !allowed.has(m.id)) continue;
    try {
      stops.push(m.background.start(ctx));
    } catch (error) {
      console.error(`The ${m.id} background did not start`, error);
    }
  }
  return () => {
    for (const stop of stops) {
      try {
        stop();
      } catch (error) {
        console.error("A background did not stop cleanly", error);
      }
    }
  };
}
